import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/ui/button';
import { COUNTDOWN_MS, countdownLabel, remainingShare, secondsLeft } from './continueModel';
import type { TutorialLesson } from './tutorialsModel';

interface LessonEndProps {
  nextLesson: TutorialLesson | null;
  /** The lesson ended while playing and the viewer has not stopped it: count down to the next lesson. */
  counting: boolean;
  onReplay: () => void;
  onCancel: () => void;
  onNextLesson: (id: string) => void;
  onClose: () => void;
}

const OVERLAY = 'fade-in absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/90 p-3 text-center backdrop-blur-sm sm:gap-3 sm:p-4';

/** Milliseconds left of a countdown that starts when the component mounts; `onDone` runs once at zero. */
function useCountdown(totalMs: number, onDone: () => void): number {
  const [left, setLeft] = useState(totalMs);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const endsAt = performance.now() + totalMs;
    const id = window.setInterval(() => {
      const rest = Math.max(0, endsAt - performance.now());
      setLeft(rest);
      if (rest > 0) return;
      window.clearInterval(id);
      done.current();
    }, 200);
    return () => window.clearInterval(id);
  }, [totalMs]);
  return left;
}

/** Brings the stage into view when the countdown starts (a phone may be scrolled down to the narration). */
function useRevealOnMount() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  }, []);
  return ref;
}

/** The seconds inside a ring that empties; under reduced motion only the number changes. */
function CountdownRing({ left, seconds }: { left: number; seconds: number }) {
  const r = 18;
  const length = 2 * Math.PI * r;
  return (
    <span aria-hidden className="relative flex size-11 items-center justify-center">
      <svg viewBox="0 0 44 44" className="absolute inset-0 size-full -rotate-90">
        <circle cx="22" cy="22" r={r} fill="none" strokeWidth="3" className="stroke-surface-3" />
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={length}
          strokeDashoffset={length * (1 - remainingShare(left))}
          className="stroke-primary transition-[stroke-dashoffset] duration-200 ease-linear motion-reduce:hidden"
        />
      </svg>
      <span className="tabular text-[15px] font-semibold text-foreground">{seconds}</span>
    </span>
  );
}

/** "Next lesson in 5…": opens the next lesson at zero; Play now, Cancel or Watch again instead. */
function Countdown({ next, onGo, onCancel, onReplay }: { next: TutorialLesson; onGo: (id: string) => void; onCancel: () => void; onReplay: () => void }) {
  const left = useCountdown(COUNTDOWN_MS, () => onGo(next.id));
  const seconds = secondsLeft(left);
  const ref = useRevealOnMount();
  return (
    <div ref={ref} data-testid="lesson-countdown" data-seconds={seconds} className={`${OVERLAY} scroll-mt-36 scroll-mb-4`}>
      <CountdownRing left={left} seconds={seconds} />
      <div className="flex min-w-0 max-w-full flex-col">
        <p data-testid="lesson-countdown-label" aria-hidden className="tabular text-[15px] font-semibold text-foreground">
          {countdownLabel(seconds)}
        </p>
        <p className="truncate text-[13px] text-muted-foreground">{next.title}</p>
        <span role="status" className="sr-only">
          Next lesson, {next.title}, starts in {COUNTDOWN_MS / 1000} seconds.
        </span>
      </div>
      <div className="flex items-center justify-center gap-2">
        <Button data-testid="lesson-countdown-go" onClick={() => onGo(next.id)}>
          <Icon name="play_arrow" size={18} fill />
          Play now
        </Button>
        <Button variant="outline" data-testid="lesson-countdown-cancel" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="ghost" aria-label="Watch again" data-testid="lesson-countdown-replay" onClick={onReplay} className="max-sm:w-11 max-sm:px-0">
          <Icon name="replay" size={18} />
          <span className="max-sm:hidden">Watch again</span>
        </Button>
      </div>
    </div>
  );
}

/** "Lesson complete" after a cancel, a manual end or on the last lesson: next lesson, replay, or back to the list. */
function Finished({ nextLesson, onReplay, onNextLesson, onClose }: Omit<LessonEndProps, 'counting' | 'onCancel'>) {
  return (
    <div data-testid="lesson-finished" className={OVERLAY}>
      <Icon name="check_circle" size={32} fill className="text-succeeded" />
      <div className="min-w-0 max-w-full">
        <p className="text-[15px] font-semibold text-foreground">Lesson complete</p>
        <p className="truncate text-[13px] text-muted-foreground">{nextLesson ? `Up next: ${nextLesson.title}` : 'That was the last lesson.'}</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {nextLesson ? (
          <Button data-testid="lesson-next-lesson" onClick={() => onNextLesson(nextLesson.id)}>
            Next lesson
            <Icon name="arrow_forward" size={18} />
          </Button>
        ) : (
          <Button onClick={onClose}>All tutorials</Button>
        )}
        <Button variant="outline" onClick={onReplay}>
          <Icon name="replay" size={18} />
          Watch again
        </Button>
      </div>
    </div>
  );
}

/** What covers the stage when a lesson ends: the countdown to the next lesson, or the completion card. */
export function LessonEnd({ counting, ...props }: LessonEndProps) {
  if (counting && props.nextLesson) return <Countdown next={props.nextLesson} onGo={props.onNextLesson} onCancel={props.onCancel} onReplay={props.onReplay} />;
  return <Finished nextLesson={props.nextLesson} onReplay={props.onReplay} onNextLesson={props.onNextLesson} onClose={props.onClose} />;
}
